import type {
  OverviewDateRange,
  OverviewFrequency,
  OverviewGroup,
  OverviewSeriesMap,
} from '../models/overview.types'

import {
  buildMultiMetricChartOptions,
  type MultiMetricChartOptions,
  type ReportColumn,
  type ReportRow,
} from '@/features/reports'

export const SUMMARY_OVERVIEW_DEFAULT_METRICS = [
  'cost',
  'purchase',
  'purchase_revenue',
  'roas',
  'revenue',
  'sessions',
  'conversions',
  'view_cart',
  'add_to_cart',
  'view_item',
  'begin_checkout',
  'view_item_list',
  'page_view',
  'first_visit',
] as const

export const SUMMARY_OVERVIEW_DEFAULT_CARD_COUNT = 4

/**
 * Keep the four production KPI slots stable even when controller metric order changes. When the
 * budget schedule flags `highlighted_metrics`, those open as the rail instead — v3
 * `SummaryDashboardChart.fetchBudgetData` seeded `selectedMetrics` from them, and the chart then
 * sorted the response by that list and showed as many cards as it named.
 */
export function selectDefaultOverviewMetricFields(
  metrics: readonly Pick<ReportColumn, 'field'>[],
  highlighted: readonly string[] = [],
): string[] {
  const available = new Set(metrics.map((metric) => metric.field))
  const flagged = [...new Set(highlighted)]
  const highlightedAvailable = flagged.filter((field) => available.has(field))
  // None of the flagged metrics came back (e.g. report-50 conversion mode) → stock KPI slots.
  const useHighlighted = highlightedAvailable.length > 0
  const prioritized = useHighlighted
    ? highlightedAvailable
    : SUMMARY_OVERVIEW_DEFAULT_METRICS.filter((field) => available.has(field))
  const prioritizedSet = new Set<string>(prioritized)
  const remaining = metrics
    .map((metric) => metric.field)
    .filter((field) => !prioritizedSet.has(field))
  return [...prioritized, ...remaining].slice(
    0,
    useHighlighted ? flagged.length : SUMMARY_OVERVIEW_DEFAULT_CARD_COUNT,
  )
}

export interface NormalizedConversionRows {
  rows: ReportRow[]
  metrics: string[]
}

export interface OverviewChartInput {
  series: OverviewSeriesMap
  forecast?: OverviewSeriesMap | null
  metrics: readonly ReportColumn[]
  activeMetricFields: readonly string[]
  activePlatform: string
  frequency: OverviewFrequency
  showForecast: boolean
  formatCell: (value: unknown, column: ReportColumn) => string
  formatAxisValue?: (value: number) => string
  formatDate: (value: string | number | Date) => string
  previousPeriodLabel: string
  forecastLabel: string
}

function numeric(value: unknown): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function bucketIdentity(row: ReportRow, frequency: OverviewFrequency): string {
  return [row[frequency], row.year, row.platform].map((value) => String(value ?? '')).join('|')
}

/** Converts report-50's event-per-row wire shape into the metric-per-bucket shape used by 06. */
export function normalizeConversionRows(
  rows: readonly ReportRow[],
  group: OverviewGroup,
  frequency: OverviewFrequency,
): NormalizedConversionRows {
  const buckets = new Map<string, ReportRow>()
  const metrics = new Set<string>()

  for (const source of rows) {
    const event = String(source.event_name ?? '')
      .trim()
      .toLowerCase()
    if (!event) continue
    const platform = source[group] ?? source.platform ?? 'all'
    const row = { ...source, platform }
    const key = bucketIdentity(row, frequency)
    const bucket = buckets.get(key) ?? {
      [frequency]: source[frequency],
      year: source.year,
      platform,
      is_previous: source.is_previous,
    }
    bucket[event] = numeric(bucket[event]) + numeric(source.event_count)
    metrics.add(event)

    if (source.event_value != null) {
      const valueMetric = `${event}_value`
      bucket[valueMetric] = numeric(bucket[valueMetric]) + numeric(source.event_value)
      metrics.add(valueMetric)
    }
    if (source.is_key_event) {
      bucket.conversions = numeric(bucket.conversions) + numeric(source.event_count)
      metrics.add('conversions')
    }
    buckets.set(key, bucket)
  }

  return { rows: [...buckets.values()], metrics: [...metrics] }
}

export function overviewDateRange(queryString: string): OverviewDateRange | null {
  const query = new URLSearchParams(queryString)
  const start = query.get('date_start')
  const end = query.get('date_end')
  return start && end ? { start, end } : null
}

export function overviewDateKey(row: Readonly<ReportRow>, frequency: OverviewFrequency): string {
  if (frequency === 'year')
    return `${String(row.year ?? row.year_of_date ?? row.yearly ?? '')}-01-01`
  if (frequency === 'month_of_year') {
    const raw = String(row.month_of_year ?? row.month ?? '')
    const parsed = new Date(`${raw} 1, ${String(row.year ?? '')}`)
    return Number.isNaN(parsed.getTime())
      ? raw
      : `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-01`
  }
  return String(row[frequency] ?? row.date ?? '')
}

export function mergeOverviewSeries(
  ...sources: readonly (OverviewSeriesMap | null)[]
): OverviewSeriesMap | null {
  const entries = sources.flatMap((source) => (source ? Object.entries(source) : []))
  return entries.length ? Object.fromEntries(entries) : null
}

export function availableOverviewPlatforms(series: OverviewSeriesMap | null): string[] {
  if (!series) return []
  return [...new Set(Object.values(series).map((item) => item.platform))].sort((left, right) => {
    if (left === 'all') return -1
    if (right === 'all') return 1
    return left.localeCompare(right)
  })
}

/** Maps Summary domain series into module 08's central multi-metric chart authority. */
export function buildSummaryOverviewChartOptions(
  input: OverviewChartInput,
): MultiMetricChartOptions {
  const visibleMetrics = input.metrics.filter((metric) =>
    input.activeMetricFields.includes(metric.field),
  )

  return buildMultiMetricChartOptions({
    frequency: input.frequency,
    previousPeriodLabel: input.previousPeriodLabel,
    forecastLabel: input.forecastLabel,
    formatDate: input.formatDate,
    series: visibleMetrics.flatMap((metric, index) => {
      const key = `${input.activePlatform}_${metric.field}`
      const actual = input.series[key]
      if (!actual) return []
      const forecast = input.showForecast ? input.forecast?.[key] : undefined
      return [
        {
          field: metric.field,
          label: metric.header,
          color: `var(--ui-chart-summary-${(index % 5) + 1})`,
          current: actual.current,
          previous: actual.before,
          ...(forecast ? { forecast: forecast.current } : {}),
          formatValue: (value: number | null | undefined) => input.formatCell(value, metric),
          ...(input.formatAxisValue ? { formatAxisValue: input.formatAxisValue } : {}),
        },
      ]
    }),
  })
}
